import { useCallback, useRef } from "react";

export const HEIGHT_TRANSITION_MS = 300;
/** How long past the transition's own length to wait for `transitionend` before giving up on it. */
export const TRANSITION_FALLBACK_MS = 100;
const HEIGHT_EASING = "cubic-bezier(0.16, 1, 0.3, 1)";
/** A change smaller than this is rounding, not content appearing or going away. */
const MIN_ANIMATED_CHANGE_PX = 1;
/** A height change this soon after the window was resized came from the resize, not from the content. */
const WINDOW_RESIZE_SETTLE_MS = 150;

interface HeightAnimatorOptions {
  /** Put on the node for as long as its height is transitioning. */
  animatingClassName?: string;
}

export interface HeightAnimator {
  destroy: () => void;
}

const NO_ANIMATOR: HeightAnimator = { destroy: () => {} };

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Makes a box whose height is `auto` grow and shrink to its new height, instead of jumping, when
 * its content changes.
 *
 * `height: auto` cannot be transitioned, so when the box's size changes - reported by a
 * `ResizeObserver`, after layout but before paint - the box is pinned at the height it had, laid
 * out there, and then given the new height in pixels for the transition to run to. When it ends
 * the pin is lifted and the layout is in charge again.
 *
 * The content is never animated, only the box it sits in. It lays out against the box as it
 * moves, so whatever is anchored to the bottom - a button bar - travels with the edge, and fields
 * that were added are uncovered as the box grows past them. Nothing is measured or cloned.
 */
export const createHeightAnimator = (node: HTMLElement, options: HeightAnimatorOptions = {}): HeightAnimator => {
  if (typeof ResizeObserver === "undefined") return NO_ANIMATOR;

  const { animatingClassName } = options;
  // The height the box last settled at - or, mid-transition, is on its way to.
  let restingHeight: number | undefined;
  let isAnimating = false;
  let lastWindowResizeAt = -Infinity;
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined;

  /** Takes back everything `animate` set, handing the height back to the layout. */
  const release = () => {
    clearTimeout(fallbackTimer);
    isAnimating = false;
    if (animatingClassName) node.classList.remove(animatingClassName);
    node.style.transition = "";
    node.style.height = "";
  };

  const animate = (from: number, to: number) => {
    isAnimating = true;
    restingHeight = to;
    node.style.transition = "none";
    node.style.height = `${from}px`;
    // Reading the height lays the box out at `from`; without it the browser would see only the
    // final value being set and jump to it.
    void node.offsetHeight;
    if (animatingClassName) node.classList.add(animatingClassName);
    node.style.transition = `height ${HEIGHT_TRANSITION_MS}ms ${HEIGHT_EASING}`;
    node.style.height = `${to}px`;
    // `transitionend` never comes if the box is hidden or the property did not really change.
    fallbackTimer = setTimeout(release, HEIGHT_TRANSITION_MS + TRANSITION_FALLBACK_MS);
  };

  const handleResize = () => {
    // The box's own transition resizes it on every frame; only the content is of interest. A
    // change of content during the transition is picked up once the pin is lifted, because the
    // layout then differs from the height the transition ended at.
    if (isAnimating) return;

    const height = node.offsetHeight;
    const previousHeight = restingHeight;
    restingHeight = height;

    if (previousHeight === undefined) return;
    if (Math.abs(height - previousHeight) < MIN_ANIMATED_CHANGE_PX) return;
    if (prefersReducedMotion()) return;
    if (performance.now() - lastWindowResizeAt < WINDOW_RESIZE_SETTLE_MS) return;

    animate(previousHeight, height);
  };

  const handleTransitionEnd = (event: TransitionEvent) => {
    // Descendants' transitions bubble up to here; only the box's own height ends this one.
    if (!isAnimating || event.target !== node || event.propertyName !== "height") return;
    release();
  };

  // Dragging a window edge moves the dialog's 90vh cap on every frame: the box should follow it
  // at once, not trail behind it by 300ms.
  const handleWindowResize = () => {
    lastWindowResizeAt = performance.now();
  };

  const observer = new ResizeObserver(handleResize);
  observer.observe(node);
  node.addEventListener("transitionend", handleTransitionEnd);
  window.addEventListener("resize", handleWindowResize);

  return {
    destroy: () => {
      observer.disconnect();
      node.removeEventListener("transitionend", handleTransitionEnd);
      window.removeEventListener("resize", handleWindowResize);
      if (isAnimating) release();
    },
  };
};

/**
 * Callback ref that animates the height of whatever it is attached to - see `createHeightAnimator`.
 * A callback ref rather than an effect on a ref object, because the node it belongs to comes and
 * goes with the dialog being opened and closed. Does nothing while `isEnabled` is false.
 */
export const useAnimatedHeight = (isEnabled: boolean, animatingClassName?: string) => {
  const animatorRef = useRef<HeightAnimator | null>(null);

  return useCallback(
    (node: HTMLElement | null) => {
      animatorRef.current?.destroy();
      animatorRef.current = node && isEnabled ? createHeightAnimator(node, { animatingClassName }) : null;
    },
    [isEnabled, animatingClassName]
  );
};
